import type {ReactElement} from 'react';
import React, {useContext, useEffect, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import type {PosterProductDataForProcessDesign} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {PublishingAnalyticsAction} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {DisplayType, ProductType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {ModalDetails} from '@Components/modal/modal-context';
import {LOADER_SIZE} from '@Components/circular-progress-loader';
import {setErrorMessageForPublishingPanel, setProductType, setErrorOnPublishPanel} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {PublishingLoaderScreen} from '@Panels/poster-publishing-options-panel/components/publishing-loader-screen/publishing-loader-screen';
import {getUserPremiumLevel} from '@Libraries/user.library';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import {useHashedId, usePrintProduct, useShowLoader} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {processDesignForPublishing} from '@Panels/poster-publishing-options-panel/publishing-process-design-library';
import {PublishingProgressBar} from '@Panels/poster-publishing-options-panel/components/publishing-progress-bar/publishing-progress-bar';
import {
  fetchPublishAsWebpageMeta,
  handlePublishRequest,
  handleUnpublishRequest,
} from '@Panels/poster-publishing-options-panel/components/publishing-as-webpage-panel/publishing-as-webpage-library';
import {useAppDispatch} from '@/hooks';
import styles from './publishing-as-webpage-panel.module.scss';
import type {PublishingAsWebpagePanelProps, PublishingAsWebpageResponse} from './publishing-as-webpage-panel.types';
import {PublishingAsWebpageType} from './publishing-as-webpage-panel.types';
import {CopyLinkButton} from '@Components/copy-link-button/copy-link-button';

export function PublishingAsWebpagePanel({...props}: PublishingAsWebpagePanelProps): ReactElement {
  const {displayType, publishItemType = PublishingAsWebpageType.PUBLISH_POSTER} = props;
  const [published, setPublished] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoadScreen, setShowLoadScreen] = useState(true);
  const [webpageLink, setWebpageLink] = useState('');
  const modalDetails = useContext(ModalDetails);
  const inMobile = modalDetails?.isMobileModal;
  const dispatch = useAppDispatch();
  const showLoader = useShowLoader();
  const userPremiumLevel = getUserPremiumLevel();
  const printProduct: PosterProductDataForProcessDesign | undefined = usePrintProduct();
  const isModalDisplay = displayType === DisplayType.MODAL;

  const hashedIdFromStore = useHashedId();
  const hashedId = props.hashedId ?? hashedIdFromStore;

  useEffect(() => {
    if (!showLoader) {
      setShowLoadScreen(true);
      fetchInitPublishAsWebpageMeta();
    }
  }, [showLoader]);

  const onFetchPublishAsWebpageMetaSuccess = (response: PublishingAsWebpageResponse): void => {
    setWebpageLink(response.shareableUrl);
    setPublished(response.isPublishedLink);
  };

  const fetchInitPublishAsWebpageMeta = (): void => {
    fetchPublishAsWebpageMeta(publishItemType, hashedId)
      .then((response: PublishingAsWebpageResponse) => {
        onFetchPublishAsWebpageMetaSuccess(response);
      })
      .catch(() => {
        dispatch(setErrorMessageForPublishingPanel(window.i18next.t('pmwjs_publish_webpage_error')));
      })
      .finally(() => {
        setShowLoadScreen(false);
      });
  };

  useEffect(() => {
    const prepareCheckout = document.querySelector('.js-prepare-share-hosted-link-purchase');
    if (prepareCheckout) {
      prepareCheckout.addEventListener('click', handleHostedLinkCheckout);
    }
    return (): void => {
      if (prepareCheckout) {
        prepareCheckout.removeEventListener('click', handleHostedLinkCheckout);
      }
    };
  }, [showLoadScreen]);

  const onPublishSuccess = (): void => {
    setPublished(!published);
    if (copied && !published) {
      setCopied(false);
    }
    setIsLoading(false);
  };

  const onPublishError = (): void => {
    dispatch(setErrorMessageForPublishingPanel(window.i18next.t('pmwjs_publish_webpage_publish_error')));
    setIsLoading(false);
  };
  const onPublishShareLink = (): void => {
    setIsLoading(true);
    window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SHARE_HOSTED_LINK_PUBLISH);
    void handlePublishRequest(publishItemType, hashedId).then(onPublishSuccess).catch(onPublishError);
  };

  const onUnPublishShareLink = (): void => {
    setIsLoading(true);
    setCopied(false);
    window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SHARE_HOSTED_LINK_UNPUBLISH);
    void handleUnpublishRequest(publishItemType, hashedId).then(onPublishSuccess).catch(onPublishError);
  };

  const getInputAndHeading = (): ReactElement => {
    return (
      <div>
        <div className="spacing-m-b-1">
          <Text val={window.i18next.t('pmwjs_webpage_link')} className="body-xs-bold spacing-p-b-3 content-body" />
        </div>
        <InputField
          isDisabled={!published}
          isReadOnly
          placeholder={webpageLink}
          input={published ? webpageLink : ''}
          inputClassName={`body-xs-regular ${styles.inputFieldPadding}`}
          information={published ? '' : window.i18next.t('pmwjs_webpage_publish_info')}
        />
        {userPremiumLevel === USER_PREMIUM_LEVELS.PAYG && printProduct?.needsPurchase ? (
          <div className="flex-v-row spacing-p-t-8">
            <Text val={window.i18next.t('pmwjs_remove_watermark')} bold size={TextSize.XXSMALL} className="spacing-m-b-1 content-body" />
            <Text
              val={window.i18next.t('pmwjs_pay_amount_or_upgrade_premium', {price: printProduct?.price, redirectionLink: window.PMW.util.site_url('/premium')})}
              size={TextSize.XXSMALL}
              dangerouslySetInnerHTML
              className="content-body"
            />
          </div>
        ) : null}
      </div>
    );
  };

  const handleHostedLinkCheckout = (e: Event): void => {
    e.stopPropagation();

    if (e.target instanceof HTMLElement) {
      dispatch(setProductType(ProductType.PRINT));
      processDesignForPublishing(printProduct ?? ({} as PosterProductDataForProcessDesign));
    }
  };

  const getCopyButton = (): ReactElement => {
    return (
      <CopyLinkButton
        linkToCopy={webpageLink}
        doChangeIconOnCopy
        buttonProps={{
          type: copied ? Type.SUCCESS : Type.PRIMARY,
          text: copied ? window.i18next.t('pmwjs_insights_keywords_copied') : window.i18next.t('pmwjs_copy_link'),
          size: Size.SMALL,
          disabled: !published,
          onClick: () => {
            setCopied(true);
          },
          isActive: false,
        }}
        onCopyFeedbackComplete={() => {
          setCopied(false);
        }}
      />
    );
  };

  const getPublishButton = (): ReactElement => {
    return !published ? (
      <Button
        customClasses="_cursor-pointer"
        onClick={onPublishShareLink}
        type={Type.PRIMARY}
        size={Size.SMALL}
        iconPlacement={IconPlacement.RIGHT}
        text={window.i18next.t('pmwjs_publish')}
        icon="icon-publish-arrow"
        isLoading={isLoading}
      />
    ) : (
      <Button
        customClasses="_cursor-pointer"
        onClick={onUnPublishShareLink}
        type={Type.SECONDARY}
        size={Size.SMALL}
        iconPlacement={IconPlacement.RIGHT}
        text={window.i18next.t('pmwjs_unpublish')}
        icon="icon-unlink"
        isLoading={isLoading}
      />
    );
  };

  const getPanelStyles = (): string => {
    if (inMobile) {
      return 'flex-v-row spacing-p-3';
    } else {
      return isModalDisplay ? 'flex-v-row spacing-p-t-8 spacing-p-b-8 spacing-p-r-12 spacing-p-l-12' : 'flex-v-row spacing-p-t-6';
    }
  };

  const getPanelContent = (): ReactElement => {
    return showLoader ? (
      <PublishingProgressBar />
    ) : (
      <>
        {showLoadScreen ? '' : getInputAndHeading()}

        {showLoadScreen ? (
          <PublishingLoaderScreen customHeight={isModalDisplay ? styles.customModalHeight : styles.customDropDownHeight} loaderSize={LOADER_SIZE.LARGE} />
        ) : (
          <div className={`flexbox justify-content-between ${!isModalDisplay || inMobile ? 'spacing-m-t-6' : 'spacing-m-t-8'}`}>
            {getCopyButton()}
            {getPublishButton()}
          </div>
        )}
      </>
    );
  };
  return <div className={`${getPanelStyles()} ${!showLoadScreen ? 'animated fadeIn' : ''}`}>{getPanelContent()}</div>;
}
